import { NodeDataSection } from '@/components/ui/NodeDataDisplay'
import { Copy } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { z } from 'zod'

interface FramerObjectDisplayProps {
  title: string
  data: any
  icon?: ReactNode
  showCopyButton?: boolean
  showExpandButton?: boolean
  defaultExpanded?: boolean
  className?: string
}

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text)
}

/**
 * Component for rendering complex Framer objects with structured display
 * Uses NodeDataDisplay for consistent styling and type-aware rendering
 */
export function FramerObjectDisplay({
  title,
  data,
  icon,
  showCopyButton = true,
  showExpandButton = true,
  defaultExpanded = false,
  className = '',
}: FramerObjectDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [copied, setCopied] = useState(false)

  // Handle null/undefined data
  if (data === null || data === undefined) {
    return (
      <div className={`rounded-lg border border-gray-200 bg-gray-50 p-3 ${className}`}>
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-gray-600">{title}</span>
          <span className="text-xs text-gray-500">(empty)</span>
        </div>
      </div>
    )
  }

  // Handle primitive values
  if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
    return (
      <div className={`rounded-lg border border-gray-200 bg-gray-50 p-3 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-sm font-medium text-gray-600">{title}</span>
          </div>
          {showCopyButton && (
            <button
              onClick={async () => {
                await copyToClipboard(String(data))
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }}
              className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              title="Copy value"
            >
              <Copy className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="mt-2 text-sm text-gray-800">{String(data)}</div>
      </div>
    )
  }

  // Handle arrays and objects
  const isObject = typeof data === 'object' && !Array.isArray(data)
  const isArray = Array.isArray(data)
  const itemCount = isArray ? data.length : isObject ? Object.keys(data).length : 0

  // Create a simple schema for the data to enable NodeDataDisplay
  const createSchemaForData = (value: any): z.ZodTypeAny => {
    if (value === null) return z.null()
    if (Array.isArray(value)) {
      const elementType = value.length > 0 ? createSchemaForData(value[0]) : z.unknown()
      return z.array(elementType)
    }
    if (typeof value === 'object') {
      const shape: Record<string, z.ZodTypeAny> = {}
      for (const [key, val] of Object.entries(value)) {
        shape[key] = createSchemaForData(val)
      }
      return z.object(shape)
    }
    if (typeof value === 'string') return z.string()
    if (typeof value === 'number') return z.number()
    if (typeof value === 'boolean') return z.boolean()
    return z.unknown()
  }

  const dataSchema = createSchemaForData(data)
  const jsonString = JSON.stringify(data, null, 2)

  return (
    <div className={`rounded-lg border border-gray-200 bg-white ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-3">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-gray-900">{title}</span>
          <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
            {isArray ? 'array' : isObject ? 'object' : typeof data}
          </span>
          {itemCount > 0 && <span className="text-xs text-gray-500">({itemCount} items)</span>}
        </div>

        <div className="flex items-center gap-1">
          {showCopyButton && (
            <button
              onClick={async () => {
                await copyToClipboard(jsonString)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              title={copied ? 'Copied!' : 'Copy JSON'}
            >
              <Copy className={`h-4 w-4 ${copied ? 'text-green-600' : ''}`} />
            </button>
          )}

          {showExpandButton && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                ▶
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-3">
          <NodeDataSection
            title=""
            schema={dataSchema}
            data={data}
            allowVariables={false}
            editMode={false}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Helper function to detect if a string contains JSON data
 */
export function isJsonString(str: string): boolean {
  try {
    const parsed = JSON.parse(str)
    return typeof parsed === 'object' && parsed !== null
  } catch {
    return false
  }
}

/**
 * Helper function to safely parse JSON with fallback
 */
export function safeJsonParse(str: string, fallback: any = null): any {
  try {
    return JSON.parse(str)
  } catch {
    return fallback
  }
}
